import React, { useEffect, useState } from 'react'
import type { MaterialCategory } from '../AdminPage'
import { useTranslation } from '@structura/i18n'

interface MaterialsSummary {
  steel: number
  concrete: number
  timber: number
  reinforcement: number
  prestressing: number
  total: number
}

interface SteelMaterial {
  id: number
  grade: string
  subgrade: string
  productForm: string
  thicknessMax: number
  yieldStrength: number
  ultimateStrengthMin: number
  ultimateStrengthMax: number
  elasticModulus: number
  density: number
  code: string
  annex: string
}

interface ConcreteMaterial {
  id: number
  class: string
  fck: number
  fckCube: number
  fcm: number
  fctm: number
  fctk005: number
  fctk095: number
  elasticModulus: number
  density: number
  poissonRatio: number
  gammaC: number
  alphaCC: number
  fcd: number
  fctd: number
  code: string
  annex: string
}

interface TimberMaterial {
  id: number
  kind: string
  class: string
  fmK: number
  ft0K: number
  ft90K: number
  fc0K: number
  fc90K: number
  fvK: number
  e0Mean: number
  e90Mean: number
  gMean: number
  density: number
  code: string
  annex: string
}

interface ReinforcementSteel {
  id: number
  grade: string
  ductilityClass: string
  productForm: string
  fyk: number
  ftk: number
  elasticModulus: number
  density: number
  k: number
  epsilonUk: number
  code: string
  annex: string
}

interface PrestressingSteel {
  id: number
  grade: string
  productForm: string
  relaxationClass: number
  fpk: number
  fp01k: number
  elasticModulus: number
  density: number
  code: string
  annex: string
}

export interface MaterialsSectionProps {
  allowedCategories: readonly MaterialCategory[]
  getSessionHeaders: () => HeadersInit
}

const MaterialsSection: React.FC<MaterialsSectionProps> = ({ allowedCategories, getSessionHeaders }) => {
  const { t } = useTranslation()
  const [materialsLoading, setMaterialsLoading] = useState<boolean>(false)
  const [materialsError, setMaterialsError] = useState<string | null>(null)
  const [materialsSummary, setMaterialsSummary] = useState<MaterialsSummary | null>(null)
  const [steelMaterials, setSteelMaterials] = useState<SteelMaterial[]>([])
  const [concreteMaterials, setConcreteMaterials] = useState<ConcreteMaterial[]>([])
  const [timberMaterials, setTimberMaterials] = useState<TimberMaterial[]>([])
  const [reinforcementMaterials, setReinforcementMaterials] = useState<ReinforcementSteel[]>([])
  const [prestressingMaterials, setPrestressingMaterials] = useState<PrestressingSteel[]>([])
  const [selectedMaterialCategory, setSelectedMaterialCategory] = useState<MaterialCategory>(allowedCategories[0])

  useEffect(() => {
    const loadMaterialsSummary = async (): Promise<void> => {
      setMaterialsLoading(true)
      setMaterialsError(null)
      try {
        const response = await fetch('/api/Materials/summary', {
          headers: getSessionHeaders()
        })

        if (!response.ok) {
          throw new Error(t('admin.materials.errLoadSummaryHttp', { status: response.status }))
        }

        const summary: MaterialsSummary = await response.json()
        setMaterialsSummary(summary)
      } catch (err) {
        const message = (err as Error).message || t('admin.materials.errLoadSummary')
        setMaterialsError(message)
      } finally {
        setMaterialsLoading(false)
      }
    }

    loadMaterialsSummary()
  }, [])

  const loadMaterialsForCategory = async (category: string): Promise<void> => {
    setMaterialsLoading(true)
    setMaterialsError(null)
    try {
      const response = await fetch(`/api/Materials/${category}`, {
        headers: getSessionHeaders()
      })

      if (!response.ok) {
        throw new Error(t('admin.materials.errLoadCategoryHttp', { category, status: response.status }))
      }

      const materials = await response.json()

      switch (category) {
        case 'steel':
          setSteelMaterials(materials)
          break
        case 'concrete':
          setConcreteMaterials(materials)
          break
        case 'timber':
          setTimberMaterials(materials)
          break
        case 'reinforcement':
          setReinforcementMaterials(materials)
          break
        case 'prestressing':
          setPrestressingMaterials(materials)
          break
      }
    } catch (err) {
      const message = (err as Error).message || t('admin.materials.errLoadCategory', { category })
      setMaterialsError(message)
    } finally {
      setMaterialsLoading(false)
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="bi bi-gear-wide-connected me-2"></i>
          {t('admin.materials.title')}
        </h5>
      </div>
      <div className="card-body">
        <p className="text-muted">
          {t('admin.materials.introBefore')} <code>Materials.db</code>{t('admin.materials.introAfter')}
        </p>

        {materialsLoading && (
          <div className="d-flex align-items-center mb-3">
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <span>{t('admin.materials.loading')}</span>
          </div>
        )}

        {materialsError && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {materialsError}
            <button type="button" className="btn-close" onClick={() => setMaterialsError(null)}></button>
          </div>
        )}

        {materialsSummary && (
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card bg-light">
                <div className="card-body">
                  <h6 className="mb-3">{t('admin.materials.librarySummary')}</h6>
                  <div className="row text-center">
                    <div className="col">
                      <div className="fs-3 fw-bold text-primary">{materialsSummary.steel}</div>
                      <div className="small text-muted">{t('admin.materials.steelGrades')}</div>
                    </div>
                    <div className="col">
                      <div className="fs-3 fw-bold text-secondary">{materialsSummary.concrete}</div>
                      <div className="small text-muted">{t('admin.materials.concreteClasses')}</div>
                    </div>
                    <div className="col">
                      <div className="fs-3 fw-bold text-success">{materialsSummary.timber}</div>
                      <div className="small text-muted">{t('admin.materials.timberClasses')}</div>
                    </div>
                    <div className="col">
                      <div className="fs-3 fw-bold text-warning">{materialsSummary.reinforcement}</div>
                      <div className="small text-muted">{t('admin.materials.reinforcement')}</div>
                    </div>
                    <div className="col">
                      <div className="fs-3 fw-bold text-info">{materialsSummary.prestressing}</div>
                      <div className="small text-muted">{t('admin.materials.prestressing')}</div>
                    </div>
                    <div className="col">
                      <div className="fs-3 fw-bold text-dark">{materialsSummary.total}</div>
                      <div className="small text-muted">{t('admin.materials.total')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mb-3">
          <div className="btn-group" role="group">
            {allowedCategories.includes('steel') && (
              <button
                type="button"
                className={`btn ${selectedMaterialCategory === 'steel' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => {
                  setSelectedMaterialCategory('steel')
                  if (steelMaterials.length === 0) loadMaterialsForCategory('steel')
                }}
              >
                <i className="bi bi-box-seam me-1"></i> {t('admin.materials.btnSteel')}
              </button>
            )}
            {allowedCategories.includes('concrete') && (
              <button
                type="button"
                className={`btn ${selectedMaterialCategory === 'concrete' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => {
                  setSelectedMaterialCategory('concrete')
                  if (concreteMaterials.length === 0) loadMaterialsForCategory('concrete')
                }}
              >
                <i className="bi bi-bricks me-1"></i> {t('admin.materials.btnConcrete')}
              </button>
            )}
            {allowedCategories.includes('timber') && (
              <button
                type="button"
                className={`btn ${selectedMaterialCategory === 'timber' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => {
                  setSelectedMaterialCategory('timber')
                  if (timberMaterials.length === 0) loadMaterialsForCategory('timber')
                }}
              >
                <i className="bi bi-tree me-1"></i> {t('admin.materials.btnTimber')}
              </button>
            )}
            {allowedCategories.includes('reinforcement') && (
              <button
                type="button"
                className={`btn ${selectedMaterialCategory === 'reinforcement' ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={() => {
                  setSelectedMaterialCategory('reinforcement')
                  if (reinforcementMaterials.length === 0) loadMaterialsForCategory('reinforcement')
                }}
              >
                <i className="bi bi-grid-3x2 me-1"></i> {t('admin.materials.btnReinforcement')}
              </button>
            )}
            {allowedCategories.includes('prestressing') && (
              <button
                type="button"
                className={`btn ${selectedMaterialCategory === 'prestressing' ? 'btn-info' : 'btn-outline-info'}`}
                onClick={() => {
                  setSelectedMaterialCategory('prestressing')
                  if (prestressingMaterials.length === 0) loadMaterialsForCategory('prestressing')
                }}
              >
                <i className="bi bi-bezier2 me-1"></i> {t('admin.materials.btnPrestressing')}
              </button>
            )}
          </div>
        </div>

        {selectedMaterialCategory === 'steel' && steelMaterials.length > 0 && (
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">{t('admin.materials.steelTitle')}</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0 table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>{t('admin.materials.thGrade')}</th>
                      <th>{t('admin.materials.thSubgrade')}</th>
                      <th>{t('admin.materials.thProductForm')}</th>
                      <th className="text-end">t ≤ [mm]</th>
                      <th className="text-end">fy [MPa]</th>
                      <th className="text-end">fu,min [MPa]</th>
                      <th className="text-end">E [GPa]</th>
                      <th className="text-end">ρ [kg/m³]</th>
                    </tr>
                  </thead>
                  <tbody>
                    {steelMaterials.map((mat) => (
                      <tr key={mat.id}>
                        <td><strong>{mat.grade}</strong></td>
                        <td>{mat.subgrade || '—'}</td>
                        <td className="text-capitalize">{mat.productForm}</td>
                        <td className="text-end">{mat.thicknessMax.toFixed(0)}</td>
                        <td className="text-end">{mat.yieldStrength.toFixed(0)}</td>
                        <td className="text-end">{mat.ultimateStrengthMin.toFixed(0)}</td>
                        <td className="text-end">{(mat.elasticModulus / 1e9).toFixed(0)}</td>
                        <td className="text-end">{mat.density.toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer text-muted">
              <small>{t('admin.materials.footerSteel', { count: steelMaterials.length })}</small>
            </div>
          </div>
        )}

        {selectedMaterialCategory === 'concrete' && concreteMaterials.length > 0 && (
          <div className="card">
            <div className="card-header bg-secondary text-white">
              <h6 className="mb-0">{t('admin.materials.concreteTitle')}</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0 table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>{t('admin.materials.thClass')}</th>
                      <th className="text-end">fck [MPa]</th>
                      <th className="text-end">fck,cube [MPa]</th>
                      <th className="text-end">fcm [MPa]</th>
                      <th className="text-end">fctm [MPa]</th>
                      <th className="text-end">Ecm [GPa]</th>
                      <th className="text-end">fcd [MPa]</th>
                      <th className="text-end">γc</th>
                    </tr>
                  </thead>
                  <tbody>
                    {concreteMaterials.map((mat) => (
                      <tr key={mat.id}>
                        <td><strong>{mat.class}</strong></td>
                        <td className="text-end">{mat.fck.toFixed(0)}</td>
                        <td className="text-end">{mat.fckCube.toFixed(0)}</td>
                        <td className="text-end">{mat.fcm.toFixed(1)}</td>
                        <td className="text-end">{mat.fctm.toFixed(2)}</td>
                        <td className="text-end">{(mat.elasticModulus / 1e9).toFixed(1)}</td>
                        <td className="text-end">{mat.fcd.toFixed(1)}</td>
                        <td className="text-end">{mat.gammaC.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer text-muted">
              <small>{t('admin.materials.footerConcrete', { count: concreteMaterials.length })}</small>
            </div>
          </div>
        )}

        {selectedMaterialCategory === 'timber' && timberMaterials.length > 0 && (
          <div className="card">
            <div className="card-header bg-success text-white">
              <h6 className="mb-0">{t('admin.materials.timberTitle')}</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0 table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>{t('admin.materials.thKind')}</th>
                      <th>{t('admin.materials.thClass')}</th>
                      <th className="text-end">fm,k [MPa]</th>
                      <th className="text-end">ft,0,k [MPa]</th>
                      <th className="text-end">fc,0,k [MPa]</th>
                      <th className="text-end">fv,k [MPa]</th>
                      <th className="text-end">E0,mean [GPa]</th>
                      <th className="text-end">ρmean [kg/m³]</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timberMaterials.map((mat) => (
                      <tr key={mat.id}>
                        <td className="text-capitalize">{mat.kind}</td>
                        <td><strong>{mat.class}</strong></td>
                        <td className="text-end">{mat.fmK.toFixed(1)}</td>
                        <td className="text-end">{mat.ft0K.toFixed(1)}</td>
                        <td className="text-end">{mat.fc0K.toFixed(1)}</td>
                        <td className="text-end">{mat.fvK.toFixed(2)}</td>
                        <td className="text-end">{(mat.e0Mean / 1e9).toFixed(1)}</td>
                        <td className="text-end">{mat.density.toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer text-muted">
              <small>{t('admin.materials.footerTimber', { count: timberMaterials.length })}</small>
            </div>
          </div>
        )}

        {selectedMaterialCategory === 'reinforcement' && reinforcementMaterials.length > 0 && (
          <div className="card">
            <div className="card-header bg-warning">
              <h6 className="mb-0">{t('admin.materials.reinforcementTitle')}</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0 table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>{t('admin.materials.thGrade')}</th>
                      <th>{t('admin.materials.thDuctilityClass')}</th>
                      <th>{t('admin.materials.thProductForm')}</th>
                      <th className="text-end">fyk [MPa]</th>
                      <th className="text-end">ftk [MPa]</th>
                      <th className="text-end">Es [GPa]</th>
                      <th className="text-end">k</th>
                      <th className="text-end">εuk [%]</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reinforcementMaterials.map((mat) => (
                      <tr key={mat.id}>
                        <td><strong>{mat.grade}</strong></td>
                        <td>{mat.ductilityClass}</td>
                        <td className="text-capitalize">{mat.productForm}</td>
                        <td className="text-end">{mat.fyk.toFixed(0)}</td>
                        <td className="text-end">{mat.ftk.toFixed(0)}</td>
                        <td className="text-end">{(mat.elasticModulus / 1e9).toFixed(0)}</td>
                        <td className="text-end">{mat.k.toFixed(2)}</td>
                        <td className="text-end">{(mat.epsilonUk * 100).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer text-muted">
              <small>{t('admin.materials.footerReinf', { count: reinforcementMaterials.length })}</small>
            </div>
          </div>
        )}

        {selectedMaterialCategory === 'prestressing' && prestressingMaterials.length > 0 && (
          <div className="card">
            <div className="card-header bg-info text-white">
              <h6 className="mb-0">{t('admin.materials.prestressingTitle')}</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0 table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>{t('admin.materials.thGrade')}</th>
                      <th>{t('admin.materials.thProductForm')}</th>
                      <th className="text-end">{t('admin.materials.thRelaxationClass')}</th>
                      <th className="text-end">fpk [MPa]</th>
                      <th className="text-end">fp0.1k [MPa]</th>
                      <th className="text-end">Ep [GPa]</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prestressingMaterials.map((mat) => (
                      <tr key={mat.id}>
                        <td><strong>{mat.grade}</strong></td>
                        <td className="text-capitalize">{mat.productForm}</td>
                        <td className="text-end">{mat.relaxationClass}</td>
                        <td className="text-end">{mat.fpk.toFixed(0)}</td>
                        <td className="text-end">{mat.fp01k.toFixed(0)}</td>
                        <td className="text-end">{(mat.elasticModulus / 1e9).toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer text-muted">
              <small>{t('admin.materials.footerPrestress', { count: prestressingMaterials.length })}</small>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MaterialsSection
